import React from 'react';
import { motion } from 'framer-motion';
import { Users, Globe2, CheckCircle, AlertCircle } from 'lucide-react';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import { usePilgrimsStore } from '../../stores/pilgrimsStore';
import { useDisputesStore } from '../../stores/disputesStore';
import { computeOrgStats } from '../../lib/orgStats';
import { CountryDistributionChart } from './CountryDistributionChart';
import { useLanguage } from '../../contexts/LanguageContext';

export const LinkingStats = () => {
  const { translations } = useLanguage();
  const { links } = usePilgrimGuideStore();
  const { pilgrims } = usePilgrimsStore();
  const { disputes } = useDisputesStore();
  const linksList = Object.values(links);

  const userRole = localStorage.getItem('userRole');
  const userId   = localStorage.getItem('userId');

  // Calculate stats — for organizers, use the canonical helper so this matches
  // /organizers-overview exactly. For admins, fall back to global link counts
  // since there's no single org to compute against.
  const stats = React.useMemo(() => {
    const countries = new Set(linksList.map((l) => l.residenceCountry)).size;

    if (userRole === 'organizer' && userId) {
      const s = computeOrgStats(userId, links, pilgrims, disputes);
      return {
        // Total = passports the org has any link to AND that exist in Pilgrims
        total: s.inBooking,
        approved: s.inBookingApproved,
        disputed: s.disputed,
        countries,
      };
    }

    // Admin/global: passport-deduped, same rules as computeOrgStats.
    // "Total" = distinct passports with a Pilgrim doc match (any link status).
    // "Disputed" = from Disputes collection (active: pending/under_processing).
    const pilgrimPassports = new Set<string>();
    for (const p of Object.values(pilgrims)) {
      const pp = String((p as { Passport_Number?: string }).Passport_Number ?? '');
      if (pp) pilgrimPassports.add(pp);
    }

    const seenPassports = new Set<string>();
    let inBooking = 0;
    for (const l of linksList) {
      if (!l.passportNumber || seenPassports.has(l.passportNumber)) continue;
      seenPassports.add(l.passportNumber);
      if (pilgrimPassports.has(l.passportNumber)) inBooking++;
    }

    const activeDisputePassports = new Set<string>();
    for (const d of Object.values(disputes)) {
      const disp = d as { status?: string; passportNumber?: string };
      if ((disp.status === 'pending' || disp.status === 'under_processing') && disp.passportNumber) {
        activeDisputePassports.add(disp.passportNumber);
      }
    }

    return {
      total: inBooking,
      approved: inBooking - activeDisputePassports.size,
      disputed: activeDisputePassports.size,
      countries,
    };
  }, [linksList, userRole, userId, links, pilgrims, disputes]);

  const statItems = [
    {
      title: translations?.linkingStats?.totalPilgrims || 'Total Pilgrims',
      value: stats.total,
      icon: Users,
      color: 'primary',
    },
    {
      title: translations?.linkingStats?.countries || 'Number of Countries',
      value: stats.countries,
      icon: Globe2,
      color: 'blue',
    },
    {
      title: translations?.linkingStats?.approved || 'Approved Links',
      value: stats.approved,
      icon: CheckCircle,
      color: 'green',
    },
    {
      title: translations?.linkingStats?.disputed || 'Disputed Links',
      value: stats.disputed,
      icon: AlertCircle,
      color: 'red',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {statItems.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-white p-6 rounded-xl shadow-sm ring-1 ring-inset ${
              stat.color === 'primary' ? 'ring-primary-500/20' :
              stat.color === 'blue' ? 'ring-blue-500/20' :
              stat.color === 'green' ? 'ring-green-500/20' :
              stat.color === 'yellow' ? 'ring-yellow-500/20' :
              'ring-red-500/20'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${
                stat.color === 'primary' ? 'bg-primary-50' :
                stat.color === 'blue' ? 'bg-blue-50' :
                stat.color === 'green' ? 'bg-green-50' :
                stat.color === 'yellow' ? 'bg-yellow-50' :
                'bg-red-50'
              }`}>
                <stat.icon className={
                  stat.color === 'primary' ? 'text-primary-500' :
                  stat.color === 'blue' ? 'text-blue-500' :
                  stat.color === 'green' ? 'text-green-500' :
                  stat.color === 'yellow' ? 'text-yellow-500' :
                  'text-red-500'
                } size={24} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                <p className={`text-2xl font-bold mt-1 ${
                  stat.color === 'primary' ? 'text-primary-500' :
                  stat.color === 'blue' ? 'text-blue-500' :
                  stat.color === 'green' ? 'text-green-500' :
                  stat.color === 'yellow' ? 'text-yellow-500' :
                  'text-red-500'
                }`}>
                  {stat.value}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <CountryDistributionChart />
      </motion.div>
    </div>
  );
};
